<template>
  <v-form ref="form" lazy-validation @submit.prevent>
    <ComplaintFormTutorialInfoBox
      v-if="isTutorialPath"
      :tutorial-translation-type="tutorialTranslationType"
    />

    <ComplaintFormReadHelpReminder
      v-if="!readHelpConfirmed && hasHelpReminder"
      @confirm="readHelpConfirmed = true"
    />

    <BaseFieldset :disabled="!readHelpConfirmed && hasHelpReminder">
      <FormSection>
        <BaseHeading :level="3">{{ $t(`${translationTicketPath}.engine.progress.1`) }}</BaseHeading>

        <template v-if="hasUserAddresses">
          <v-radio-group
            v-model="formData.addressId"
            class="mb-6"
            :error-messages="addressIdErrors"
            :hide-details="!addressIdErrors.length"
            :class="$style.radios"
          >
            <template v-for="(userAddress, i) in sortedUserAddresses">
              <v-radio
                v-if="i === 0"
                :key="userAddress.id"
                color="black"
                :value="userAddress.id"
                v-on="getTouchListeners('formData.addressId')"
              >
                <template #label>
                  <address>
                    <p
                      v-for="(data, index) in userAddress.transformedData"
                      :key="index"
                      class="mb-0"
                      :class="$style.userData"
                    >
                      {{ data }}
                    </p>
                  </address>
                </template>
              </v-radio>
              <v-radio
                v-else
                v-show="showAllAddresses"
                :key="userAddress.id"
                color="black"
                :value="userAddress.id"
                v-on="getTouchListeners('formData.addressId')"
              >
                <template #label>
                  <address>
                    <p
                      v-for="(data, index) in userAddress.transformedData"
                      :key="index"
                      class="mb-0"
                      :class="$style.userData"
                    >
                      {{ data }}
                    </p>
                  </address>
                </template>
              </v-radio>
            </template>
          </v-radio-group>

          <BaseButton @base-click="showAllAddresses = !showAllAddresses">{{
            $t(showAddressesTranslations)
          }}</BaseButton>
        </template>

        <template v-else>
          <BaseAlert
            :class="$style.alert"
            :message="$t('complaintForm.ticket.no_user_addresses')"
            type="error"
            icon="info"
            outlined
          />
        </template>
      </FormSection>

      <FormSection>
        <BaseHeading :level="3">{{ $t('complaintForm.ticket.contact_person') }}</BaseHeading>

        <BaseTextfield
          v-model="formData.contactPersonName"
          class="mb-6"
          :error-messages="contactPersonNameErrors"
          :hide-details="!contactPersonNameErrors.length"
          :label="$t('complaintForm.ticket.contact_person_name')"
          required
          v-on="getTouchListeners('formData.contactPersonName')"
        />
        <BaseTextfield
          v-model="formData.contactPersonSurname"
          class="mb-6"
          :error-messages="contactPersonSurnameErrors"
          :hide-details="!contactPersonSurnameErrors.length"
          :label="$t('complaintForm.ticket.contact_person_surname')"
          required
          v-on="getTouchListeners('formData.contactPersonSurname')"
        />
        <BaseTextfield
          v-model="formData.phone"
          class="mb-6"
          :error-messages="phoneErrors"
          :hide-details="!phoneErrors.length"
          :label="$t('complaintForm.ticket.phone')"
          required
          v-on="getTouchListeners('formData.phone')"
        />
        <BaseTextfield
          v-model="formData.email"
          class="mb-6"
          :error-messages="emailErrors"
          :hide-details="!emailErrors.length"
          :label="$t('complaintForm.ticket.email')"
          required
          v-on="getTouchListeners('formData.email')"
        />
        <BaseTextfield
          v-model="formData.reference"
          class="mb-6"
          :label="$t('complaintForm.ticket.reference')"
          :hide-details="true"
        />
        <BaseTextarea
          v-model="formData.openingHours"
          :hide-details="true"
          :label="$t('complaintForm.ticket.opening_hours')"
        />
      </FormSection>

      <MyDataInfobox :compare-data="myDataCompareData" @input="processChangedUserData" />

      <ComplaintFormStepperControls
        :is-valid="isValid"
        :has-required-fields="true"
        :next-step="submit"
      />
    </BaseFieldset>
  </v-form>
</template>

<script>
import { get, isEmpty } from 'lodash-es'
import { mapMutations, mapGetters } from 'vuex'
import {
  required,
  email,
  requiredIf,
  minLength,
  maxLength,
  numeric,
} from 'vuelidate/lib/validators'

import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'

import { phoneNumber } from '@/js/components/Forms/helper/form-validation-helper.js'

import { ADDRESS_CONSTANTS, transformAddress, getJoinedAddress } from '@/js/helper/address-helper'

import StepperMixin from '@/js/mixins/stepper-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'

import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'

import APP_GETTER from '@/js/plugins/store/modules/app/getter-types.js'

import FormSection from '@/js/components/Forms/FormSection.vue'

import ComplaintFormReadHelpReminder from '@/js/components/Forms/ComplaintForm/ComplaintFormReadHelpReminder.vue'
import ComplaintFormStepperControls from '@/js/components/Forms/ComplaintForm/ComplaintFormStepperControls.vue'
import ComplaintFormTutorialInfoBox from '@/js/components/Forms/ComplaintForm/ComplaintFormTutorialInfoBox.vue'
import MyDataInfobox from '@/js/components/Usermanagement/MyDataInfobox.vue'

export default {
  name: 'ComplaintFormUserData',
  components: {
    FormSection,
    ComplaintFormReadHelpReminder,
    ComplaintFormStepperControls,
    ComplaintFormTutorialInfoBox,
    MyDataInfobox,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin],
  props: {
    ...StepperMixin.props,
  },
  data() {
    return {
      readHelpConfirmed: false,
      formData: {
        addressId: null,
        customerNo: null,
        company: null,
        address: null,
        zip: null,
        city: null,
        contactPersonName: null,
        contactPersonSurname: null,
        phone: null,
        email: null,
        reference: null,
        openingHours: null,
      },
      // validation values
      customerNoMinLength: 5,
      customerNoMaxLength: 6,
      showAllAddresses: false,
    }
  },
  validations: {
    formData: {
      addressId: {
        required,
      },
      customerNo: {
        required: requiredIf(function () {
          return !this.hasUserAddresses
        }),
        numeric,
        minLength: minLength(5),
        maxLength: maxLength(6),
      },
      company: {
        required: requiredIf(function () {
          return !this.hasUserAddresses
        }),
      },
      address: {
        required: requiredIf(function () {
          return !this.hasUserAddresses
        }),
      },
      zip: {
        required: requiredIf(function () {
          return !this.hasUserAddresses
        }),
      },
      city: {
        required: requiredIf(function () {
          return !this.hasUserAddresses
        }),
      },
      contactPersonName: {
        required,
      },
      contactPersonSurname: {
        required,
      },
      phone: {
        required,
        phoneNumber,
      },
      email: {
        required,
        email,
      },
    },
  },
  computed: {
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      language: FORM_GETTER.LANGUAGE,
      deliveryAddresses: FORM_GETTER.DELIVERY_ADDRESSES,
      userName: FORM_GETTER.USER_NAME,
      userGroup: FORM_GETTER.USER_GROUP,
      userCustomerNumber: FORM_GETTER.CUSTOMER_NUMBER,
      userFormData: FORM_GETTER.USER_FORM_DATA,
    }),
    ...mapGetters(STOREMODULES.APP, {
      getLinkByType: APP_GETTER.APP_CONFIG_LINK_BY_TYPE,
    }),
    showAddressesTranslations() {
      return this.showAllAddresses
        ? 'complaintForm.hide_all_addresses'
        : 'complaintForm.show_all_addresses'
    },
    hasUserAddresses() {
      return !isEmpty(this.deliveryAddresses)
    },
    allUserAddresses() {
      return [...this.deliveryAddresses.map(address => transformAddress(address))]
    },
    addressId() {
      return this.formData.addressId
    },
    sortedUserAddresses() {
      return [
        ...this.allUserAddresses.filter(address => address.id === this.addressId),
        ...this.allUserAddresses.filter(address => address.id !== this.addressId),
      ]
    },
    userAddressesById() {
      return this.allUserAddresses.reduce(
        (userAddressesById, useraddress) => ({
          ...userAddressesById,
          [useraddress.id]: useraddress.addressData,
        }),
        {},
      )
    },
    hasHelpReminder() {
      return false
      // return (
      //   (this.ticketType === FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.TICKET_TYPES.ENGINE &&
      //     !this.isWorkingTimeRemunerationForm) ||
      //   this.ticketType === FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.TICKET_TYPES.BATTERY
      // )
    },

    /** Form data */
    fullFormData() {
      return {
        ...this.formData,
        lang: this.language,
        ticketType: this.ticketType,
        brand: this.userGroup,
      }
    },

    /** Errors */
    addressIdErrors() {
      return this.formErrorHelper('formData.addressId', [
        {
          condition: 'required',
          error: this.addressErrorMessage,
        },
      ])
    },
    customerNoErrors() {
      return this.formErrorHelper('formData.customerNo', [
        {
          condition: 'required',
          error: this.customerNumberErrorMessage,
        },
        this.numericError,
        this.minLengthError(this.customerNoMinLength),
        this.maxLengthError(this.customerNoMaxLength),
      ])
    },
    companyErrors() {
      return this.formErrorHelper('formData.company', [
        {
          condition: 'required',
          error: this.companyErrorMessage,
        },
      ])
    },
    addressErrors() {
      return this.formErrorHelper('formData.address', [
        {
          condition: 'required',
          error: this.addressErrorMessage,
        },
      ])
    },
    zipErrors() {
      return this.formErrorHelper('formData.zip', [
        {
          condition: 'required',
          error: this.zipErrorMessage,
        },
      ])
    },
    cityErrors() {
      return this.formErrorHelper('formData.city', [
        {
          condition: 'required',
          error: this.locationErrorMessage,
        },
      ])
    },
    contactPersonNameErrors() {
      return this.formErrorHelper('formData.contactPersonName', [
        {
          condition: 'required',
          error: this.firstNameErrorMessage,
        },
      ])
    },
    contactPersonSurnameErrors() {
      return this.formErrorHelper('formData.contactPersonSurname', [
        {
          condition: 'required',
          error: this.lastNameErrorMessage,
        },
      ])
    },
    phoneErrors() {
      return this.formErrorHelper('formData.phone', [
        {
          condition: 'required',
          error: this.requiredErrorMessage,
        },
        this.phoneError,
      ])
    },
    emailErrors() {
      return this.formErrorHelper('formData.email', [
        {
          condition: 'required',
          error: this.requiredErrorMessage,
        },
        {
          condition: 'email',
          error: this.emailErrorMessage,
        },
      ])
    },

    /** helper */
    accountLink() {
      return this.getLinkByType('account')
    },
    myDataCompareData() {
      return {
        email: this.formData.email,
        // selected_address: this.formData.addressId
        data: {
          firstName: this.formData.contactPersonName,
          lastName: this.formData.contactPersonSurname,
          // mobile:
          telephone: this.formData.phone,
        },
        additional_data: {
          openings: this.formData.openingHours,
          reference: this.formData.reference,
        },
      }
    },
  },

  // TODO: Clarify whether this data is to be set implicitly!
  watch: {
    'formData.addressId'(newId, oldId) {
      if (newId === oldId) {
        return
      }

      if (this.hasUserAddresses) {
        this.formData.customerNo = this.userCustomerNumber
        this.formData.company = get(this.userAddressesById, [
          this.formData.addressId,
          ADDRESS_CONSTANTS.COMPANY,
        ])
        this.formData.address = getJoinedAddress(
          get(this.userAddressesById, this.formData.addressId),
          {},
        )
        this.formData.zip = get(this.userAddressesById, [
          this.formData.addressId,
          ADDRESS_CONSTANTS.POSTAL,
        ])
        this.formData.city = get(this.userAddressesById, [
          this.formData.addressId,
          ADDRESS_CONSTANTS.CITY,
        ])
      }
    },
  },
  created() {
    const jsonSerializedForm = JSON.parse(this.userFormData)
    const addressId = get(jsonSerializedForm, 'addressId', undefined)

    this.formData.contactPersonName = get(jsonSerializedForm, 'firstname', '')
    this.formData.contactPersonSurname = get(jsonSerializedForm, 'lastname', '')
    this.formData.phone = get(jsonSerializedForm, 'telephone', '')
    this.formData.email = get(jsonSerializedForm, 'email', '')
    this.formData.reference = get(jsonSerializedForm, 'reference', '')
    this.formData.openingHours = get(jsonSerializedForm, 'openings', '')
    const selectedAddress = this.deliveryAddresses.find(
      adr => adr[ADDRESS_CONSTANTS.SAP_ADDRESS_ID] === addressId,
    )
    this.formData.addressId = selectedAddress ? selectedAddress[ADDRESS_CONSTANTS.ID] : undefined
    this.showAllAddresses = !this.formData.addressId
  },
  methods: {
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
    }),
    submitCallback() {
      this.updateFormData(this.fullFormData)
      this.nextStep()
    },
    processChangedUserData({ user, changes = [] }) {
      const userFieldToFormdata = {
        email: 'email',
        // selected_address: 'addressId'
        'data.firstName': 'contactPersonName',
        'data.lastName': 'contactPersonSurname',
        // 'data.mobile':
        'data.telephone': 'phone',
        'additional_data.openings': 'openingHours',
        'additional_data.reference': 'reference',
      }
      changes.forEach(change => {
        const formDataField = get(userFieldToFormdata, change)

        if (!formDataField) {
          return
        }
        this.formData[formDataField] = get(user, change)
      })
    },
  },
}
</script>

<style lang="scss" module>
.radios {
  :global(.v-input__control) {
    width: 100%;
  }

  :global(.v-input--radio-group__input) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    > :global(.v-radio.v-radio.v-radio) {
      flex: 0 1 auto;
      min-width: 19rem;
      max-width: 19rem;
      margin-bottom: 2rem;
    }
  }

  :global(.v-input--radio-group__input) {
    @supports (display: grid) {
      @media screen and (min-width: map-get($grid-breakpoints, 'sm')) {
        grid-template-rows: repeat(2, auto);
        grid-template-columns: repeat(2, 1fr);
      }

      @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
        grid-template-rows: auto;
        grid-template-columns: repeat(3, 1fr);
      }

      display: grid;
      grid-gap: 2rem;
      grid-template-rows: repeat(3, auto);
      grid-template-columns: 1fr;

      > :global(.v-radio.v-radio.v-radio) {
        min-width: unset;
        max-width: unset;
        margin-bottom: 0;
      }
    }
  }
}

.userData {
  line-height: 1.5;
}
</style>
